/**
 * Connectivity classification.
 *
 * The interesting failure here is not "offline" — it is "internet is up but the judicial
 * API is unreachable", which is what a VPN or a national filter looks like. Telling the
 * two apart matters because the advice differs: check your Wi-Fi, versus turn off your VPN.
 *
 * Two no-cors probes disambiguate them. A no-cors fetch resolves if the host answers at
 * all and rejects on a network-level failure, which is exactly the signal needed — no
 * CORS headers required on either end.
 */

/** The app's own origin: reachable whenever the device genuinely has internet. */
const CONTROL_URL = () => window.location.origin + "/";

/** The judicial API host — what a VPN or filter would block. */
const PROBE_URL = () => {
  const base = process.env.VUE_APP_BASEURL;
  // An empty base means local dev behind the devServer proxy; probing our own origin
  // there is the honest answer, since that is what actually serves /jud.
  if (!base) return CONTROL_URL();
  return base.replace(/\/+$/, "") + "/";
};

const PROBE_TIMEOUT_MS = 6500;

function probeHost(url) {
  return new Promise((resolve) => {
    let settled = false;
    const controller = "AbortController" in window ? new AbortController() : null;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      if (controller) {
        try {
          controller.abort();
        } catch (e) {
          /* aborting is best-effort */
        }
      }
      resolve(false);
    }, PROBE_TIMEOUT_MS);

    const finish = (result) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(result);
    };

    fetch(url, {
      mode: "no-cors",
      cache: "no-store",
      signal: controller ? controller.signal : undefined,
    })
      .then(() => finish(true))
      .catch(() => finish(false));
  });
}

/**
 * @returns {Promise<"ok"|"offline"|"server">}
 *   `offline` — no internet at all.
 *   `server`  — internet is up, but the API host does not answer (VPN / filter / outage).
 *   `ok`      — both reachable.
 */
export function checkConnection() {
  // navigator.onLine === false is a reliable fast path for airplane mode. The `true`
  // case is famously unreliable, so it is not treated as evidence of anything.
  if (navigator.onLine === false) return Promise.resolve("offline");

  return probeHost(CONTROL_URL()).then((netOk) => {
    if (!netOk) return "offline";
    return probeHost(PROBE_URL()).then((apiOk) => (apiOk ? "ok" : "server"));
  });
}

/** Cheap synchronous read, for the ambient banner. */
export function isOffline() {
  return navigator.onLine === false;
}
